/**
 * English catalogue (launch locale). Keys are stable identifiers used by the bot, Mini App and
 * web; product packs may override them via `copy_ref`. Placeholders use `{name}` syntax.
 * Adding Hindi later = a new catalogue with the same keys (enforced by `checkCatalogue`).
 *
 * Telegram rule (spec §9.1): bot and Mini App copy never mention INR prices, the website or
 * another payment method. A CI test scans this catalogue for that.
 */
export const en = {
    'common.error.generic': 'Something went wrong on our side. Please try again in a moment.',
    'common.error.rate_limited': 'You are going a bit fast. Please wait {seconds} seconds and try again.',
    'common.back': '« Back',
    'common.cancel': 'Cancel',
    'common.done': 'Done',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.on': 'on',
    'common.off': 'off',
    'common.not_set': 'not set',
    // ── onboarding (spec §3.1) ────────────────────────────────────────────────────────────
    'onboarding.welcome': 'Hi {name}! I am {product}: a daily practice coach for SSC exams, right here in Telegram.',
    'onboarding.consent.prompt': 'Before we start: we store your Telegram ID, exam choice and practice history to coach you. You can export or delete your data any time in /settings. Read /privacy and /terms. You must be 18 or older.',
    'onboarding.consent.accept': "I'm 18+ and agree",
    'onboarding.consent.decline': 'No thanks',
    'onboarding.consent.declined': 'No problem. Nothing about you is stored apart from your Telegram ID. You can still read /privacy and /terms, and send /start whenever you change your mind.',
    'account.paused': 'This account is paused. Contact support.',
    'onboarding.limited': 'Send /start and accept the notice to begin practising.',
    'onboarding.exam.prompt': 'Which exam are you preparing for?',
    'onboarding.variant.branch': 'Which branch?',
    'onboarding.variant.level': 'Which level?',
    'onboarding.variant.variant': 'Which post?',
    'onboarding.date.prompt': 'When is your exam?\n{window}',
    'onboarding.date.window': 'Upcoming: {exam} {cycle}: {start} – {end}',
    'onboarding.date.unknown': 'The next dates for {exam} are not announced yet.',
    'onboarding.date.this': 'Yes, this one',
    'onboarding.date.next': 'Next cycle',
    'onboarding.date.unsure': 'Not sure',
    'onboarding.date.pick_month': 'Pick a month',
    'onboarding.date.month_prompt': 'Pick the month of your exam:',
    'onboarding.minutes.prompt': 'How many minutes a day can you practise?',
    'onboarding.minutes.option': '{minutes} min',
    'onboarding.diagnostic.intro': "Let's find your level: {count} quick questions, one per subject. No pressure!",
    'onboarding.diagnostic.empty': 'Questions for your exam are still being added, so we will skip the warm-up for now.',
    'onboarding.plan.ready': 'Your plan to {examDate}: {minutes} min/day. Today: {task}.',
    'onboarding.plan.no_date': 'your exam',
    'onboarding.menu_hint': 'Use the buttons below any time. /help lists everything.',
    'onboarding.nudge': 'You were almost set up. Send /start to pick up where you left off.',
    // No price, mock promise or spots count here: /upgrade shows what is on sale today.
    'renewal.monthly': 'Your Monthly Pass ends in {days} days. Renew now to keep Pro: /upgrade',
    'onboarding.resume': 'Welcome back! Let us continue where you left off.',
    // ── main menu (spec §3.2) ─────────────────────────────────────────────────────────────
    'menu.today': '🔥 Daily Challenge',
    'menu.practice': '🎯 Pick a Topic',
    'menu.mock': '🧪 Mock',
    'menu.weak': '📉 Weak areas',
    'menu.plan': '🗓 Plan',
    'menu.doubt': '❓ Ask a doubt',
    'menu.shown': 'What would you like to do?',
    // Short free text that is not a doubt (keyword rules, no AI).
    'freetext.pyq_style': 'We have PYQ-style practice in every subject. Tap Daily Challenge to start.',
    'menu.welcome_back': 'Welcome back, {name}! 🔥 Streak: {streak} days.',
    // ── practice and the daily loop (spec §3.3) ───────────────────────────────────────────
    'practice.daily.intro': "Daily Challenge is here! {count} questions, about 10 minutes. Let's go 💪",
    'practice.daily.reminder': 'Your streak is waiting 🔥 Daily Challenge is not finished yet.',
    'practice.daily.continue': 'Continue',
    'practice.daily.done_today': "You have finished today's Daily Challenge ✅ Come back tomorrow, or pick a topic with /practice.",
    'practice.empty': 'There are no questions here yet. Pick a topic that has questions:',
    'practice.empty.none': 'There are no questions for your exam yet. You can ask a doubt with /doubt.',
    'practice.empty.daily': 'There are no questions for your exam yet, so there is no Daily Challenge yet; we will send it once questions are ready. Pick a topic that has questions or ask a doubt with /doubt.',
    'practice.subject.soon_tapped': 'There are no {subject} questions yet. Here is what you can practise now:',
    'practice.pick_topic': '🎯 Pick a Topic',
    'practice.question_prefix': 'Q{n}/{total}. ',
    'practice.report': '⚠️ Report',
    'practice.explain': '💡 Show explanation',
    'practice.explain.unscored': '💡 Explanation\n\n{text}\n\nThis question will not count towards your score, streak or stats, since you saw the solution first. You can still answer it to check yourself.',
    'practice.explain.shown': '💡 Explanation\n\n{text}',
    'practice.explain.none': 'Sorry, this question does not have a written explanation yet. Answer it as usual and it will count normally.',
    // After a wrong answer in practice (never mocks, the SSC Rankers League or the diagnostic).
    'practice.wrong': 'Not quite. The right answer is {answer}.',
    'practice.wrong.explained': '{wrong}\n\n💡 {text}',
    // The button under a set's intro message: the first question only comes after it is pressed.
    'practice.start': 'Start ▶',
    // What a learner picked in a menu: the tapped menu message is edited into this line.
    'tap.chosen': '{label}: {choice}',
    'tap.chosen_line': '✔ {choice}',
    'tap.subject': 'Subject',
    'tap.topic': 'Topic',
    'tap.exam': 'Exam',
    'tap.branch': 'Branch',
    'tap.level': 'Level',
    'tap.post': 'Post',
    'tap.exam_date': 'Exam date',
    'tap.exam_month': 'Exam month',
    'tap.minutes': 'Minutes a day',
    'tap.daily_time': 'Daily time',
    'tap.report': 'Report',
    'tap.your_answer': 'Your answer',
    'tap.delete': 'Delete account',
    'practice.explain.unavailable': 'Explanations are not available for this question here.',
    'practice.detailed': '📖 Detailed solution',
    'practice.summary': 'Done! You scored {correct}/{total}. {streakLine}',
    'practice.summary.streak': '🔥 Streak: {streak} days.',
    'practice.summary.viewed_one': 'Done! {correct}/{scored} scored ({viewed} viewed solution). {streakLine}',
    'practice.summary.viewed': 'Done! {correct}/{scored} scored ({viewed} viewed solutions). {streakLine}',
    'practice.summary.no_credit': 'No streak credit this time: every answer came after viewing its solution.',
    'practice.summary.no_credit_skipped': 'No streak credit this time: a day counts only when every question of Daily Challenge is answered.',
    'practice.summary.topics': 'By topic:\n{lines}',
    'practice.more_topic': '🎯 Practise a topic',
    'practice.more_mistakes': '🔁 Re-practise mistakes',
    'practice.subject.prompt': 'Pick a subject:',
    'practice.topic.prompt': 'Pick a topic in {subject}:',
    'practice.topic.any': 'Mixed ({subject})',
    'practice.starting': 'Starting {count} questions on {topic}.',
    'practice.limit': 'You have used today’s {limit} free practice questions. Come back tomorrow, or see /upgrade for up to 400 practice questions a day.',
    'practice.active_doubt': 'You are in the middle of a practice set. Ask this as a doubt?',
    'practice.ask_as_doubt': 'Yes, ask as a doubt',
    'practice.need_onboarding': 'Let us set you up first: send /start.',
    // ── reports (spec §5.6) ───────────────────────────────────────────────────────────────
    'report.prompt': 'What is wrong with this question?',
    'report.reason.wrong_answer': 'Wrong answer',
    'report.reason.ambiguous': 'Ambiguous',
    'report.reason.typo': 'Typo',
    'report.reason.out_of_syllabus': 'Out of syllabus',
    'report.reason.other': 'Other',
    'report.thanks': 'Thanks! We will check this question. You will hear from us when it is fixed.',
    'report.resolved': 'Thanks for your report: the question you flagged has been reviewed and fixed. 🙏',
    // ── weak areas and plan ───────────────────────────────────────────────────────────────
    'weak.title': 'Your weakest topics:',
    'weak.line': '{n}. {topic}: {accuracy}% correct ({attempts} answered)',
    'weak.none': 'Not enough answers yet. Do a few practice sets and I will show your weak spots. Start with a topic:',
    'weak.locked': 'Pro shows your 10 weakest topics, your mistake notebook and a re-practice queue: /upgrade',
    'weak.mistakes': 'Mistakes to re-practise: {count}',
    'plan.title': 'Your plan for today ({minutes} min):',
    'plan.task.daily_set': "• Today's {questions}",
    'plan.task.topic_practice': '• {questions} questions on {topic}',
    'plan.task.review_mistakes': '• Re-practise {questions} mistakes',
    'plan.task.mock': '• A {pattern} mock',
    'plan.week': 'This week: {days}',
    'plan.exam_in': 'Exam in {days} days.',
    'plan.adjust': '⚙️ Adjust',
    // ── doubts (spec §3.2, §10) ───────────────────────────────────────────────────────────
    'doubt.prompt': 'Send your doubt as a message or a photo of the question. AI doubt credits left: {remaining}.',
    'doubt.thinking': 'Working on it… 🤔',
    'doubt.answer': '{answer}',
    'doubt.sources': 'Sources: {list}',
    'doubt.remaining': 'AI doubt credits left: {remaining}.',
    'doubt.followup_free': 'A follow-up on the same question is free. AI doubt credits left: {remaining}.',
    'doubt.low': 'You have used most of your AI doubt credits ({remaining} left). Buy more: /upgrade',
    'doubt.limit': 'You have used all your AI doubt credits. {more}',
    'doubt.limit.pack_offer': 'Get {qty} more with a Doubt Pack ({stars}⭐ early-bird / {list}⭐), or see /upgrade for a full Pro plan.',
    'doubt.limit.pack': 'Get {qty} more with a Doubt Pack ({stars}⭐), or see /upgrade for a full Pro plan.',
    'doubt.limit.no_pack': 'See /upgrade for a full Pro plan.',
    'doubt.unreadable': 'I could not read a question in that photo. Try a sharper photo, or type it.',
    'doubt.too_short': 'Please write your doubt in a little more detail.',
    'doubt.unavailable': 'I cannot answer doubts right now. Please try again in a while.',
    'doubt.photo_too_big': 'That photo is too large. Please send a smaller one.',
    'doubt.helpful': '👍 Helpful',
    'doubt.not_helpful': '👎 Not helpful',
    'doubt.rated': 'Thanks for the feedback!',
    'doubt.confirm': 'Shall I answer this as a doubt? You have {remaining} AI doubt credits left.',
    // ── settings and privacy (spec §12.2) ─────────────────────────────────────────────────
    'settings.title': 'Settings\nExam: {exam}\nExam date: {date}\nDaily Challenge at: {time}\nMinutes/day: {minutes}\nReminders: {reminders}\nPaused: {paused}',
    'settings.change_exam': '🎯 Change exam',
    'settings.daily_time': '⏰ Daily time',
    'settings.minutes': '⏱ Minutes/day',
    'settings.reminders': '🔔 Reminders: {state}',
    'settings.pause': '⏸ Pause 7 days',
    'settings.resume': '▶️ Resume',
    'settings.export': '📦 Export my data',
    'settings.delete': '🗑 Delete my account',
    'settings.subscription': '💳 Manage subscription',
    'settings.time_prompt': 'When should Daily Challenge arrive?',
    'settings.saved': 'Saved ✅',
    'settings.exported': 'Here is all the data we hold about you.',
    'settings.delete_confirm': 'Delete your account? Your progress, doubts and settings will be erased. This cannot be undone.',
    'settings.delete_yes': 'Yes, delete everything',
    'settings.deleted': 'Your account has been deleted. Send /start if you ever want to come back. 👋',
    'settings.no_subscription': 'You have no active subscription.',
    // ── info commands ─────────────────────────────────────────────────────────────────────
    // `{mock}` is `help.mock_open` while `mocks_enabled` is on, else empty: the mock commands are listed only then.
    'help.text': 'Here is what I can do:\n/today: today’s Daily Challenge\n/practice: pick a topic to practise{mock}\n/weak: your weak areas\n/plan: your study plan\n/doubt: ask a doubt (text or photo)\n/invite: invite friends\n/upgrade: Pro plans\n/link: attach a purchase made elsewhere\n/settings: exam, time, reminders, your data\n/support, /paysupport, /terms, /privacy',
    'help.mock_open': '\n/mock: full mocks\n/rank: your last ranked mock\n/monthlyexam: SSC Rankers League',
    'terms.text': 'Terms of use (summary): {product} is a practice service for exam preparation. Questions are original concept-level practice items, not official papers. Paid plans are digital services; refunds within {refundHours} hours if less than {refundPercent}% used. Full terms: ask /support for a copy. Operator: {entity}.',
    'privacy.text': 'Privacy notice: we store your Telegram ID, name, exam choice and practice history to coach you, and use AI providers to answer doubts (your name is never sent). We never sell data. Export or delete everything in /settings. Grievance contact: {email}.',
    'support.prompt': 'Write your message for our support team and send it here. Question about the SSC Rankers League? Start with "SSC Rankers League" so it reaches the right team.',
    'support.paysupport_prompt': 'Payment problem? Send the payment reference (from your Telegram receipt) and describe the issue in one message.',
    'support.email_line': 'You can also email {email}.',
    'support.created': 'Thanks! Ticket #{ref} is open. We usually reply within 24 hours.',
    'support.reply': 'Reply from support (ticket #{ref}):\n{text}',
    'grandmock.soon': 'No SSC Rankers League is running right now. Keep practising with /today.',
    'grandmock.none': "No SSC Rankers League is announced yet. We'll announce each exam date in advance; there's usually one SSC Rankers League every month.",
    'grandmock.next': '🏆 SSC Rankers League: {title}\nStarts {when} (entry open for {join} min). Results {results}.',
    'grandmock.live': "🟢 It's live now. Entry closes {closes}.",
    'grandmock.ended': 'This SSC Rankers League has ended. Results come out {results}.',
    'grandmock.results_out': '📊 Results are out. Open the SSC Rankers League to see the leaderboard.',
    'grandmock.cancelled': 'This SSC Rankers League was cancelled: {reason}',
    'grandmock.eligible_pass': '✅ You can take part with your pass.',
    'grandmock.eligible_open': '✅ You can take part.',
    'grandmock.streak': '🔥 Streak {done}/{required} days in a row, keep going to unlock the SSC Rankers League. Answer every question of Daily Challenge each day; a missed day resets the count to 0.',
    'grandmock.streak_today': 'Today still counts: send /today.',
    'grandmock.streak_done': "✅ Streak complete: you've unlocked the SSC Rankers League.",
    'grandmock.streak_broken': 'Not enough days are left before this SSC Rankers League to reach the streak, so free entry is closed for this one. The count starts fresh after the exam. You can still take part with a pass: /upgrade',
    'grandmock.needs_pass': 'The SSC Rankers League is open to pass holders. See /upgrade.',
    'grandmock.rules': 'Rules: {url}',
    'grandmock.reminded': "🔔 Done. We'll remind you before the SSC Rankers League.",
    'grandmock.remind_button': '🔔 Remind me',
    'grandmock.open_button': 'Open SSC Rankers League',
    'upgrade.soon': 'Pro plans are not available in the bot right now.',
    'link.soon': 'Linking a purchase is not available right now.',
    'link.usage': 'Send /link followed by your code, for example: /link ABCD-EFGH',
    'upgrade.title': '⭐ Pro plans, paid with Telegram Stars\n\n{lines}\n\nPro unlocks up to 400 practice questions a day, your 10 weakest topics, mistake re-practice, AI doubt credits{mocks}.',
    // {mocks} in upgrade.title: the mock promise only while `mocks_enabled` is on (as on the web), else empty.
    'upgrade.mocks_open': ' and every mock',
    'upgrade.line': '• {name}: {price}{note}',
    'upgrade.price': '{stars} ⭐',
    'upgrade.note_doubts': ' · {n} AI doubt credits',
    // Never an end date, "limited time", a cap or a spots count on an offer (product rule, ADR 0042).
    'upgrade.offer_regular': '🏷 {label}: the struck-through price is the regular price.',
    'upgrade.subscribe_offer': '🏷 {label}: {stars} ⭐ a month instead of {list} ⭐. Your subscription renews at {stars} ⭐ until you cancel.',
    'upgrade.note_monthly': ' a month, cancel any time',
    'upgrade.note_credits': ' ({qty} {what})',
    'upgrade.what_mock': 'mocks',
    'upgrade.what_doubt': 'AI doubt credits',
    'upgrade.button': '{name} · {stars} ⭐',
    'upgrade.status_pro': '✅ You have Pro until {date}.',
    'upgrade.status_credits': 'Mock credits: {mock} · Doubt credits: {doubt}',
    'upgrade.status_doubt_credits': 'Doubt credits: {doubt}',
    'upgrade.need_exam': 'Pick your exam in /settings first, then come back to /upgrade.',
    'upgrade.subscribe': 'Tap below to start your monthly Pro (renews every 30 days until you cancel):',
    'upgrade.subscribe_button': 'Subscribe · {stars} ⭐ a month',
    'upgrade.unavailable': 'That plan is not available right now. Send /upgrade to see current plans.',
    // Purchase coverage (ADR 0039): what a plan would add for this learner.
    'upgrade.cover_included': ' · ✅ Included in your {name} (until {date})',
    'upgrade.cover_subscribed': ' · 🔁 Your subscription already renews (until {date})',
    'upgrade.cover_extends': ' · adds {days} days after {date}',
    'upgrade.cover_switch': '\n  Starts today. Your {exam} pass (until {date}) is not extended: its leftover days are not added.',
    'upgrade.cover_cancel': ' To stop paying for it, cancel its renewal with /subscription.',
    'upgrade.covered': "✅ You're already covered until {date}, so this would add nothing. Need more doubts? Get a Doubt Pack from /upgrade.",
    'upgrade.covered_subscription': '🔁 Your {name} already renews until {date}, so a second subscription would charge you twice. Need more doubts? Get a Doubt Pack from /upgrade.',
    'pay.precheckout.covered': "You're already covered for this plan, so this payment would add nothing. Send /upgrade to see your options.",
    'pay.precheckout.expired': 'This invoice has expired. Send /upgrade for a fresh one.',
    'pay.precheckout.invalid': 'This payment could not be checked. Please send /upgrade and try again.',
    'pay.thanks': '✅ Payment received: {plan} is active{until}.\nReceipt: {ref}\nProblem with a payment? /paysupport (refunds within {hours} hours if under {percent}% used).',
    'pay.thanks_until': ' until {date}',
    'pay.renewed': '✅ Your monthly Pro renewed. Active until {date}.',
    'link.done': '✅ Linked! {count} purchase item(s) are now on this Telegram account.',
    'link.invalid': 'That code is not valid or has expired. Check it and try again.',
    'link.rate_limited': 'Too many tries. Please wait an hour and try again.',
    'subscription.active': 'Monthly Pro renews automatically. Current period ends {date}.',
    'subscription.cancel_button': 'Cancel renewal',
    'subscription.cancelled': 'Renewal cancelled. Pro stays active until {date}.',
    'subscription.cancel_failed': 'Could not cancel right now. Please try again, or use /paysupport.',
    'mock.open': 'Open the mock hub to take timed mocks with an all-India rank:',
    'mock.button': '🧪 Open mocks',
    'mock.soon': 'Mocks are not available in this chat. Keep practising with /today.',
    // Mocks switched on but none open or scheduled for this exam yet.
    'mock.empty': "We're adding questions. Mocks will show here once they are ready. Keep your prep going with today’s Daily Challenge (/today) and topic practice:",
    // Any mock or SSC Rankers League command while `mocks_enabled` is off.
    'mock.off': 'Mocks are not available yet. Keep your prep going with today’s Daily Challenge (/today) and topic practice:',
    'cards.soon': 'Flashcards are not available right now.',
    'cards.due': '🗂 You have {n} flashcards due. Review them in the app:',
    'cards.none_due': '🗂 Nothing due right now. You can start some new flashcards in the app:',
    'cards.done_today': '🗂 All done for today. Come back tomorrow for more flashcards.',
    'cards.free_sampler': 'On the Free plan you get a {n}-card sampler. Every deck comes with Monthly, Till Exam and Annual plans.',
    'cards.open_button': '🗂 Review flashcards',
    'ca.soon': 'The daily current-affairs digest is not available right now.',
    'rank.none': 'You have not taken a ranked mock yet. The weekly live mock runs Saturday 10:00 to Sunday 22:00.',
    'rank.none_soon': 'You have no mock results yet. Keep your Daily Challenge streak going with /today.',
    'rank.pending': '{title}: your rank appears once results are published.',
    'rank.final': '🏆 {title}\nAll-India rank {rank} of {cohort} · percentile {percentile}\nScore {score} / {max}',
    'rank.provisional': '⏳ {title}\nProvisional rank {rank} of {cohort} · percentile {percentile}\nScore {score} / {max}\nThe final rank comes when results are published.',
    'rank.predicted': 'Predicted exam score: {low}–{high}',
    'invite.text': 'Invite friends preparing for SSC! When 3 of them finish setup and their first Daily Challenge, you get a free mock. Your link:\n{link}\nProgress: {count}/3',
    // The invite texts without the free-mock reward, while `mocks_enabled` is off.
    'invite.text_plain': 'Invite friends preparing for SSC! Share your link:\n{link}\nFriends joined: {count}',
    'invite.progress_plain': 'Invite friends preparing for SSC! Share your link:\n{link}\n\nFriends joined: {joined}',
    'invite.progress': 'Invite friends preparing for SSC! When {per} of them finish setup and their first Daily Challenge, you get a free mock (up to {cap} a month).\n\nYour link:\n{link}\n\nFriends joined: {joined} · qualified: {qualified}\nTowards the next free mock: {toward}/{per}\nFree mocks earned this month: {month}/{cap}',
    'referral.rewarded': '🎁 {qty} free mock added! {per} of your friends finished their first Daily Challenge. Use it from /mock.',
    'ca.today_title': '📰 Current affairs, {date}',
    'ca.none_today': 'Today’s current-affairs one-liners are not out yet. They come around 1 pm.',
    'ca.digest_button': '✍️ Practise in the bot',
    'growth.digest_title': '📰 Current affairs · {date}',
    // ── generic fallbacks ─────────────────────────────────────────────────────────────────
    'bot.unknown_command': 'I did not get that. /help lists everything I can do.',
    'bot.group_only_commands': 'Message me privately to practise: tap my name and press Start.',
    'admin.login.title': 'Sign in to the admin portal',
    'miniapp.home.title': 'SSC exam practice',
    'miniapp.open_in_telegram': 'Open this page from the Telegram bot to continue.',
    'miniapp.loading': 'Loading…',
    'miniapp.error': 'Something went wrong. Please try again.',
    'miniapp.retry': 'Try again',
    'miniapp.account_paused': 'This account is paused. Contact support.',
    'miniapp.start_bot_first': 'Open the bot and press Start first, then come back here.',
    'miniapp.session_expired': 'Your session ended. Close this window and open it again from the bot.',
    'miniapp.unauthorized.code': '401 · Not signed in',
    'miniapp.unauthorized.title': 'Sign in to continue',
    'miniapp.unauthorized.body': "You're not signed in, or your session has expired.",
    'miniapp.unauthorized.sign_in': 'Sign in again',
    'miniapp.unauthorized.home': 'Go home',
    'miniapp.hub.live': 'Weekly live mock',
    'miniapp.hub.live_window': 'Open until {end}',
    'miniapp.hub.live_next': 'Next live mock starts {start}',
    'miniapp.hub.live_none': 'No live mock this week yet.',
    'miniapp.hub.live_done': 'You have taken this week’s live mock. Results: {at}',
    'miniapp.hub.library': 'Mock library',
    'miniapp.hub.library_empty': 'No mocks available for your exam yet.',
    'miniapp.hub.stage_tier': 'Tier {n}',
    'miniapp.hub.stage_paper': 'Paper {n}',
    'miniapp.hub.resume': 'Resume your mock',
    'miniapp.hub.results': 'My results',
    'miniapp.hub.results_empty': 'Your results will appear here.',
    'miniapp.mock.meta': '{questions} questions · {minutes} min',
    'miniapp.mock.start': 'Start',
    'miniapp.ins.title': 'Instructions',
    'miniapp.ins.sections': 'Sections',
    'miniapp.ins.marking': '+{marks} for a right answer, −{negative} for a wrong one',
    'miniapp.ins.qualifying': 'Qualifying only',
    'miniapp.ins.timers': 'Each part has its own timer: {parts}. When a part’s time ends it locks and the next one opens.',
    'miniapp.ins.timer_one': 'The timer runs on our server. If time runs out, your answers are submitted automatically.',
    'miniapp.ins.calculator': 'An on-screen calculator is available.',
    'miniapp.ins.ranked': 'This is a ranked mock: one attempt only. Your rank and solutions appear when results are published.',
    'miniapp.ins.offline': 'Answers save automatically, even if your network drops for a while.',
    'miniapp.ins.agree': 'I have read the instructions',
    'miniapp.ins.begin': 'Begin mock',
    'miniapp.player.question': 'Question {n}',
    'miniapp.player.mark': 'Mark for review',
    'miniapp.player.unmark': 'Unmark',
    'miniapp.player.clear': 'Clear',
    'miniapp.player.prev': 'Previous',
    'miniapp.player.next': 'Save & next',
    'miniapp.player.palette': 'Questions',
    'miniapp.player.submit': 'Submit',
    'miniapp.player.next_part': 'Finish this part',
    'miniapp.player.next_part_confirm': 'Lock this part and move on? You cannot come back to it.',
    'miniapp.player.confirm': 'Submit now? Answered {answered}, marked {marked}, not answered {left}.',
    'miniapp.player.offline': 'Offline: {n} answers waiting to save',
    'miniapp.player.saving': 'Saving…',
    'miniapp.player.saved': 'Saved',
    'miniapp.player.save_failed': 'We could not finish saving or submit this part. Your answers are kept on this device. Check your connection and try again.',
    'miniapp.player.locked': 'Time is up for this part. Moving on.',
    'miniapp.player.calculator': 'Calculator',
    'miniapp.player.tables': 'Reference tables',
    'miniapp.player.report': 'Report a problem',
    'miniapp.player.report_note': 'Anything to add? (optional)',
    'miniapp.player.report_send': 'Send report',
    'miniapp.player.report_cancel': 'Cancel',
    'miniapp.player.report_failed': 'We could not send your report. Please try again.',
    'miniapp.legend.answered': 'Answered',
    'miniapp.legend.not_answered': 'Not answered',
    'miniapp.legend.not_visited': 'Not visited',
    'miniapp.legend.marked': 'Marked',
    'miniapp.legend.answered_marked': 'Answered & marked',
    'miniapp.result.title': 'Your result',
    'miniapp.result.score': 'Score {score} / {max}',
    'miniapp.result.normalised': 'Normalised score {score}',
    'miniapp.result.counts': '{right} right · {wrong} wrong · {skipped} skipped',
    'miniapp.result.rank': 'All-India rank {rank} of {cohort}',
    'miniapp.result.rank_provisional': 'Provisional rank {rank} of {cohort} (final rank at results time)',
    'miniapp.result.percentile': 'Percentile {p}',
    'miniapp.result.predicted': 'Predicted score in the exam: {low}–{high}',
    'miniapp.result.sections': 'By section',
    'miniapp.result.solutions': 'Solutions',
    'miniapp.result.solutions_later': 'Solutions open when results are published ({at}).',
    'miniapp.result.your_answer': 'Your answer',
    'miniapp.result.correct_answer': 'Correct answer',
    'miniapp.result.skipped': 'Not answered',
    'miniapp.result.leaderboard': 'Top rankers',
    'miniapp.result.back': 'Back to mocks',
    'miniapp.card.share': '📸 Share my result card',
    'miniapp.card.failed': 'Could not make the card. Tap to try again.',
    'miniapp.card.brand': 'SSCExamPrep · SSC Mock Test',
    'miniapp.card.cta': 'Practise free on Telegram',
    'miniapp.cancel': 'Cancel',
    'miniapp.confirm': 'Confirm',
    'miniapp.brand.tagline': 'Learn • Practice • Succeed',
    'miniapp.hub.hello': 'Your mock tests',
    // The hub heading while `mocks_enabled` is off (nothing about mocks shows).
    'miniapp.hub.hello_no_mocks': 'Your practice',
    // Mocks switched on, but none are open or scheduled yet.
    'miniapp.hub.mocks_empty': "We're adding questions. Your mocks will show here once they are ready.",
    'miniapp.plans.title': 'Pro plans',
    'miniapp.plans.subtitle': 'Paid with Telegram Stars.',
    'miniapp.plans.have_pro': 'You have Pro until {date}.',
    'miniapp.plans.offer_regular': 'the struck-through price is the regular price.',
    'miniapp.plans.free': 'Free',
    'miniapp.plans.free_now': 'Your plan',
    'miniapp.plans.was': 'Was',
    'miniapp.plans.pro': 'Pro',
    'miniapp.plans.mocks': '{n} full mocks',
    'miniapp.plans.doubts': '{n} AI doubt credits',
    'miniapp.plans.credits_left': 'AI doubt credits left: {n}',
    'miniapp.plans.monthly': 'monthly, cancel any time',
    'miniapp.plans.days': '{n} days',
    'miniapp.plans.till_exam': 'until your exam',
    'miniapp.plans.how_to_buy': 'To buy, go back to the chat and send /upgrade.',
    'miniapp.plans.buy': 'Back to chat to buy',
    'miniapp.plans.group_plans': 'Plans',
    'miniapp.plans.group_packs': 'AI doubt packs',
    'miniapp.plans.packs_note': 'Top-ups for any plan, including Free. A pack alone does not unlock Pro.',
    'miniapp.plans.best_value': 'Best value',
    'miniapp.plans.included': 'Included in your {name} until {date}',
    'miniapp.plans.subscribed': 'Your subscription already renews (until {date})',
    'miniapp.plans.covered_next': 'Need more doubts? Get a Doubt Pack below.',
    'miniapp.plans.adds_days': 'Adds {days} days after {date}',
    'miniapp.plans.switch_note': 'Your {exam} pass ends {date}. Its leftover days are not added.',
    'miniapp.plans.cancel_hint': 'Cancel its renewal in the chat with /subscription.',
    'miniapp.plans.one_exam': 'Best for one exam',
    'miniapp.plans.per_month': '{n} ⭐/month',
    'miniapp.plans.from_per_month': 'from {n} ⭐/month',
    'miniapp.plans.save_vs_monthly': 'save {n}% vs Monthly',
    'miniapp.plans.per_credit': '{n} ⭐ per credit',
    'miniapp.plans.less_per_credit': '{n}% less per credit',
    'miniapp.result.right': 'Right',
    'miniapp.result.wrong': 'Wrong',
    'miniapp.result.skipped_short': 'Skipped',
};
//# sourceMappingURL=en.js.map